import { ConditionOperatorEnumType, CountOperator } from '@reach/shared-types/conditions';
import React, { useState } from 'react';
import type { RelatedSchemaWithFieldTypes } from '../../engage-segments-tab/segment-definitions-api';
import type { FieldInfo } from '../../engage-segments-tab/segment-definitions-api';
import { FieldConditionRow } from './FieldConditionRow';
import { NestedGroupBlock } from './NestedGroupBlock';
import { RelatedResourceBlock } from './RelatedResourceBlock';
import type {
  BuilderCondition,
  BuilderFieldCondition,
  BuilderGroup,
  BuilderNestedGroup,
  BuilderRelatedResourceCondition,
} from './segment-builder.types';
import {
  isBuilderFieldCondition,
  isBuilderNestedGroup,
  isBuilderRelatedResourceCondition,
} from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

function generateId(): string {
  return crypto.randomUUID();
}

interface ConditionGroupProps {
  group: BuilderGroup;
  contactFields: FieldInfo[];
  relatedSchemas: RelatedSchemaWithFieldTypes[];
  onUpdate: (group: BuilderGroup) => void;
  onDelete: () => void;
  disabled?: boolean;
}

export const ConditionGroup: React.FC<ConditionGroupProps> = ({
  group,
  contactFields,
  relatedSchemas,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  const [addRelatedSchemaId, setAddRelatedSchemaId] = useState('');

  const handleLogicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const logic = e.target.value === 'or' ? 'or' : 'and';
    onUpdate({ ...group, logic });
  };

  const handleConditionUpdate = (index: number) => (cond: BuilderCondition) => {
    const next = [...group.conditions];
    next[index] = cond;
    onUpdate({ ...group, conditions: next });
  };

  const handleConditionDelete = (index: number) => () => {
    const next = group.conditions.filter((_, i) => i !== index);
    onUpdate({ ...group, conditions: next });
  };

  const handleAddFieldCondition = () => {
    const firstField = contactFields[0];
    const newCond: BuilderFieldCondition = {
      id: generateId(),
      type: 'field',
      field: firstField?.name ?? '',
      fieldType: firstField?.type ?? 'unknown',
      operator: ConditionOperatorEnumType.EQUALS,
      value: [],
    };
    onUpdate({
      ...group,
      conditions: [...group.conditions, newCond],
    });
  };

  const handleAddNestedGroup = () => {
    // New nested group with opposite logic for intuitive nesting
    const newNestedGroup: BuilderNestedGroup = {
      id: generateId(),
      type: 'nested_group',
      logic: group.logic === 'and' ? 'or' : 'and',
      conditions: [],
      isExpanded: true,
    };
    onUpdate({
      ...group,
      conditions: [...group.conditions, newNestedGroup],
    });
  };

  return (
    <div className={styles.groupCard}>
      <div className={styles.groupHeader}>
        <span>Conditions combined with</span>
        <select
          className={styles.groupLogicSelect}
          value={group.logic}
          onChange={handleLogicChange}
          disabled={disabled}
          aria-label="Group logic">
          <option value="and">AND</option>
          <option value="or">OR</option>
        </select>
        <button
          type="button"
          className={styles.groupDeleteBtn}
          onClick={onDelete}
          disabled={disabled}
          aria-label="Delete group">
          Delete
        </button>
      </div>

      <div className={styles.conditionsList}>
        {group.conditions.map((cond, i) => (
          <React.Fragment key={cond.id}>
            {i > 0 && (
              <div className={styles.andConnector}>
                <span className={styles.andConnectorLabel}>{group.logic.toUpperCase()}</span>
              </div>
            )}
            {isBuilderFieldCondition(cond) ? (
              <FieldConditionRow
                condition={cond}
                fieldOptions={contactFields}
                onUpdate={c => handleConditionUpdate(i)(c)}
                onDelete={handleConditionDelete(i)}
                disabled={disabled}
              />
            ) : isBuilderRelatedResourceCondition(cond) ? (
              <RelatedResourceBlock
                condition={cond}
                relatedSchemas={relatedSchemas}
                onUpdate={c => handleConditionUpdate(i)(c)}
                onDelete={handleConditionDelete(i)}
                disabled={disabled}
              />
            ) : isBuilderNestedGroup(cond) ? (
              <NestedGroupBlock
                condition={cond}
                contactFields={contactFields}
                relatedSchemas={relatedSchemas}
                onUpdate={c => handleConditionUpdate(i)(c)}
                onDelete={handleConditionDelete(i)}
                disabled={disabled}
              />
            ) : null}
          </React.Fragment>
        ))}
      </div>

      <div className={styles.addConditionRow}>
        <button
          type="button"
          className={styles.addConditionBtn}
          onClick={handleAddFieldCondition}
          disabled={disabled || contactFields.length === 0}>
          + Add condition
        </button>
        <button
          type="button"
          className={styles.addConditionBtn}
          onClick={handleAddNestedGroup}
          disabled={disabled}>
          + Nested {group.logic === 'and' ? 'OR' : 'AND'} group
        </button>
        <select
          className={styles.selectInput}
          value={addRelatedSchemaId}
          onChange={e => {
            const id = e.target.value;
            setAddRelatedSchemaId(id);
            if (id) {
              const schema = relatedSchemas.find(s => s.id === id);
              if (schema) {
                const newCond: BuilderRelatedResourceCondition = {
                  id: generateId(),
                  type: 'related_resource',
                  schemaId: schema.id,
                  schemaName: schema.name,
                  foreignKeyPath: schema.foreignKeyPath,
                  countOperator: CountOperator.GTE,
                  countValue: 1,
                  conditions: [],
                  isExpanded: true,
                };
                onUpdate({
                  ...group,
                  conditions: [...group.conditions, newCond],
                });
                setAddRelatedSchemaId('');
              }
            }
          }}
          disabled={disabled || relatedSchemas.length === 0}
          style={{ width: 'auto', minWidth: '12rem' }}
          aria-label="Add related resource condition">
          <option value="">+ Add related resource...</option>
          {relatedSchemas.map(s => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
